<script setup lang="ts">
import { isPdfTemplate } from '~/constants/cvTemplates';
import type { CVTemplate } from '~/types';
import type { CVData } from '~/types/cv';

interface ExperienceView {
  title: string
  company: string
  startDate?: string
  endDate?: string
  description?: string
}

interface EducationView {
  school: string
  degree?: string
  startDate?: string
  endDate?: string
}

const props = defineProps<{
  cvData: CVData;
  experiencesList: ExperienceView[];
  educationList: EducationView[];
  showQRCode: boolean;
  isPremium?: boolean;
  isAdminExport?: boolean;
  preview?: boolean;
  template?: CVTemplate;
  mobileMode?: boolean;
  customScale?: number;
  /**
   * Logo pattern over the CV, for free accounts. Opt-in rather than derived
   * from `isPremium`: on a public page that prop carries the *visitor's* plan,
   * which says nothing about the CV being looked at.
   *
   * On the A4 templates it only ever adds the watermark — the server still
   * decides who is entitled to a clean sheet — so marketing previews can show
   * the free PDF as it really comes out, whoever is signed in.
   */
  watermark?: boolean;
  /**
   * Showcase preview: the A4 sheet is rendered from our own sample CV, clean.
   * It sells the template, so it carries no free-plan branding. `true` is the
   * /demo CV; a slug picks the sample of that /resume-examples page.
   */
  sample?: boolean | string;
  /** How the sheet is lifted off the desk. Marketing pages use the Sofax hairline. */
  frame?: 'shadow' | 'border';
  /** Paid guest download: renders that purchase's snapshot without a watermark. */
  guestToken?: string;
  /** A link for one company: renders the CV attached to that application. */
  linkToken?: string;
}>();

const emit = defineEmits<{
  (e: 'preview-loaded'): void;
  (e: 'preview-error', message: string): void;
}>();

// Default to classic template if not specified
const currentTemplate = computed(() => props.template || 'classic');

// Responsive scaling logic
const previewScale = ref(1);
const wrapperStyle = ref({});

function calculateScale() {
  if (!import.meta.client) return;

  if (props.customScale) {
    previewScale.value = props.customScale;
    return;
  }

  if (props.mobileMode) {
    const screenWidth = window.innerWidth;
    const a4Width = 794;
    const padding = 32; // Left and right padding
    const maxScale = 1; // Maximum scale for better readability
    const calculatedScale = (screenWidth - padding) / a4Width;
    previewScale.value = Math.min(calculatedScale, maxScale);
  } else {
    previewScale.value = 1;
  }
}

// Update wrapper style based on scaling
watchEffect(() => {
  if (props.mobileMode || props.customScale) {
    wrapperStyle.value = {
      transform: `scale(${previewScale.value})`,
      transformOrigin: 'top left',
      width: '794px',
      minHeight: '1123px',
      height: 'auto'
    };
  } else {
    wrapperStyle.value = {};
  }
});

onMounted(() => {
  nextTick(calculateScale);
  if (props.mobileMode) {
    window.addEventListener('resize', calculateScale);
  }
});

onUnmounted(() => {
  if (props.mobileMode) {
    window.removeEventListener('resize', calculateScale);
  }
});
</script>

<template>
  <div class="cv-preview-wrapper" :style="wrapperStyle">
    <!-- The A4 templates show the PDF itself, printed by the export's own
         pipeline, so the preview is the file the download hands over. -->
    <CVPreviewPaged
      v-if="isPdfTemplate(currentTemplate)"
      :cv-data="cvData"
      :template="currentTemplate"
      :is-premium="isPremium"
      :frame="frame"
      :guest-token="guestToken"
      :link-token="linkToken"
      :watermark="watermark"
      :sample="sample"
      @loaded="emit('preview-loaded')"
      @error="(message: string) => emit('preview-error', message)"
    />

    <!-- The web CV always shows the default Signature layout here; the per-CV
         choice between the layouts lives on the editor and card pages. -->
    <CVBusinessWebSignature
        v-else-if="currentTemplate === 'business-web'"
        :cv-data="cvData"
        :experiences-list="experiencesList"
        :education-list="educationList"
        :show-q-r-code="showQRCode"
      />
  </div>
</template>

<style scoped>
.cv-preview-wrapper {
  width: 100%;
  height: 100%;
}
</style>
